import { useMemo } from 'react';
import type { AnalyticsConfig } from '../types/analytics.types';
import { OFFER_NANO_ID_META_NAME } from '../../offerNanoIdMeta';

export const useAnalyticsConfig = (): AnalyticsConfig => {
  return useMemo(() => {
    // Extrai configurações dos parâmetros da URL
    const urlParams = new URLSearchParams(window.location.search);

    // GTM IDs (pode ter múltiplos)
    const gtmIds = urlParams.get('gtm_ids')?.split(',').filter(Boolean) || [];

    // GA4 IDs (pode ter múltiplos)
    const ga4Ids = urlParams.get('ga4_ids')?.split(',').filter(Boolean) || [];

    // Google Ads IDs (pode ter múltiplos)
    const adsIds = urlParams.get('ads_ids')?.split(',').filter(Boolean) || [];

    // Facebook Pixels (pode ter múltiplos)
    const facebookPixels = urlParams.get('facebook_pixels')?.split(',').filter(Boolean) || [];

    // TikTok Pixels (pode ter múltiplos)
    const tiktokPixelsFromUrl = urlParams.get('tiktok_pixels')?.split(',').filter(Boolean) || [];

    // Pinterest Tag IDs (pode ter múltiplos)
    const pinterestTagsFromUrl = urlParams.get('pinterest_tags')?.split(',').filter(Boolean) || [];

    // Outros parâmetros
    const clientSlug = urlParams.get('client_slug') || '';
    const storeId = urlParams.get('store_id') || '';

    const offerData = sessionStorage.getItem('shopifyOffer');
    let parsedOffer: Record<string, unknown> | null = null;
    try {
      if (offerData) {
        parsedOffer = JSON.parse(offerData) as Record<string, unknown>;
      }
    } catch (error) {
      console.warn('Erro ao parsear dados da oferta:', error);
    }

    const nanoIdFromMeta =
      typeof document !== 'undefined'
        ? document.querySelector(`meta[name="${OFFER_NANO_ID_META_NAME}"]`)?.getAttribute('content')?.trim() || ''
        : '';
    const rawOfferNano = parsedOffer?.nanoId;
    const nanoIdFromOffer =
      typeof rawOfferNano === 'string' && rawOfferNano.trim() ? rawOfferNano.trim() : '';

    const nanoId = nanoIdFromMeta || nanoIdFromOffer;

    let storePixels: string[] = [];
    let storeTiktokPixels: string[] = [];
    let storePinterestTags: string[] = [];

    if (parsedOffer) {
      type PixelEntry = { settings?: { pixel_id?: string } };
      const extractIds = (arr: unknown): string[] =>
        Array.isArray(arr)
          ? (arr as PixelEntry[]).map((p) => p?.settings?.pixel_id ?? '').filter(Boolean)
          : [];

      const offerPixels = parsedOffer.pixels as Record<string, unknown> | undefined;

      if (offerPixels) {
        if (!facebookPixels.length) storePixels = extractIds(offerPixels['facebook']);
        if (!gtmIds.length) gtmIds.push(...extractIds(offerPixels['gtm']));
        if (!ga4Ids.length) ga4Ids.push(...extractIds(offerPixels['google-analytics']));
        if (!adsIds.length) adsIds.push(...extractIds(offerPixels['google-ads']));
        if (!tiktokPixelsFromUrl.length) storeTiktokPixels = extractIds(offerPixels['tiktok']);
        if (!pinterestTagsFromUrl.length) storePinterestTags = extractIds(offerPixels['pinterest']);
      }
    }

    const config = {
      gtm_ids: gtmIds,
      ga4_ids: ga4Ids,
      ads_ids: adsIds,
      facebook_pixels: [...facebookPixels, ...storePixels],
      tiktok_pixels: [...tiktokPixelsFromUrl, ...storeTiktokPixels],
      pinterest_tags: [...pinterestTagsFromUrl, ...storePinterestTags],
      client_slug: clientSlug,
      storeId,
      nanoId,
    };

    return config;
  }, []);
};
